import { HDate, Locale, gematriya, calendar, flags } from '@hebcal/core';
import { CalendarOptions } from './calendar-options';
import { frameMm, makePhotoElement, makeShapeElement, makeTextElement } from './elements';
import type { AlbumDocument, Element, TextStyle } from './schema';

export function currentCalendarYear(system: CalendarOptions['system'], today = new Date()): number {
  return system === 'hebrew' ? new HDate(today).getFullYear() : today.getFullYear();
}

export function calendarMonthNumbers(options: CalendarOptions): number[] {
  if (options.system === 'gregorian') return Array.from({ length: 12 }, (_, i) => i + 1);
  return [...Array.from({ length: HDate.monthsInYear(options.year) - 6 }, (_, i) => i + 7), 1, 2, 3, 4, 5, 6];
}

export interface CalendarDay {
  iso: string;
  weekday: number;
  primary: string;
  secondary: string;
  holidays: string[];
}

/** Date arithmetic uses absolute days, never milliseconds or UTC parsing across DST boundaries. */
export function calendarMonth(options: CalendarOptions, month: number, lang: 'he' | 'en') {
  const opts = CalendarOptions.parse(options);
  if (!calendarMonthNumbers(opts).includes(month)) throw new RangeError('Invalid calendar month');
  const hebrew = opts.system === 'hebrew';
  const first = hebrew ? new HDate(1, month, opts.year) : new HDate(new Date(opts.year, month - 1, 1));
  const length = hebrew ? HDate.daysInMonth(month, opts.year) : new Date(opts.year, month, 0).getDate();
  const locale = lang === 'he' ? 'he' : 'en';
  const events = calendar({ start: first, end: new HDate(first.abs() + length - 1), il: opts.region === 'israel', noModern: !opts.israeliHolidays, noSpecialShabbat: true });
  const holidays = new Map<number, string[]>();
  for (const event of events) {
    const modern = Boolean(event.getFlags() & flags.MODERN_HOLIDAY);
    if (modern ? !opts.israeliHolidays : !opts.jewishHolidays) continue;
    const day = event.getDate().abs();
    holidays.set(day, [...(holidays.get(day) ?? []), event.render(locale)]);
  }
  const gregMonth = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' });
  const shortGreg = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short' });
  const days: CalendarDay[] = Array.from({ length }, (_, index) => {
    const hd = new HDate(first.abs() + index);
    const gd = hd.greg();
    const heDay = lang === 'he' ? gematriya(hd.getDate()) : String(hd.getDate());
    const heMonth = Locale.gettext(hd.getMonthName(), locale);
    return {
      iso: `${gd.getFullYear()}-${String(gd.getMonth() + 1).padStart(2, '0')}-${String(gd.getDate()).padStart(2, '0')}`,
      weekday: hd.getDay(),
      primary: hebrew ? heDay : String(gd.getDate()),
      secondary: opts.dualDates ? (hebrew ? shortGreg.format(gd) : `${heDay} ${heMonth}`) : '',
      holidays: holidays.get(hd.abs()) ?? [],
    };
  });
  const title = hebrew
    ? `${Locale.gettext(first.getMonthName(), locale)} ${lang === 'he' ? gematriya(opts.year) : opts.year}`
    : gregMonth.format(first.greg());
  const otherMonthYear = (hd: HDate) => hebrew ? gregMonth.format(hd.greg())
    : `${Locale.gettext(hd.getMonthName(), locale)} ${lang === 'he' ? gematriya(hd.getFullYear()) : hd.getFullYear()}`;
  const subtitle = opts.dualDates ? [...new Set([otherMonthYear(first), otherMonthYear(new HDate(first.abs() + length - 1))])].join(' – ') : '';
  return { title, subtitle, days, offset: (first.getDay() - opts.weekStart + 7) % 7 };
}

/** Materialize dates so the existing editor, undo, backups and PDF renderer all use the same content. */
export function populateCalendar(doc: AlbumDocument, options: CalendarOptions): AlbumDocument {
  const opts = CalendarOptions.parse(options);
  const months = calendarMonthNumbers(opts);
  const rtl = doc.meta.lang === 'he';
  const w = doc.page.widthMm;
  const h = doc.page.heightMm;
  const margin = Math.min(w, h) * 0.045;
  const gridWidth = w - margin * 2;
  const col = gridWidth / 7;
  const gridY = h * 0.52;
  const row = (h * 0.40) / 6;
  const fontScale = Math.min(w / 210, h / 250);
  const weekdays = rtl ? ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let monthIndex = 0;
  const spreads = doc.spreads.map((spread) => {
    const elements: Element[] = [];
    const sides = spread.single ? [spread.single] : doc.cover.binding === 'rtl' ? ['right', 'left'] : ['left', 'right'];
    for (const side of sides) {
      const x0 = side === 'right' ? w : 0;
      const text = (content: string, x: number, y: number, width: number, height: number, size: number, style: Partial<TextStyle> = {}) => {
        elements.push(makeTextElement({
          frame: frameMm(x0 + x, y, width, height), locked: true, z: 2,
          content: { paragraphs: [content] },
          style: { fontFamily: 'Heebo', fontSizePt: Math.max(4, size * fontScale), direction: rtl ? 'rtl' : 'ltr', align: 'start', lineHeight: 1.15, ...style },
        }));
      };
      const month = months[monthIndex++];
      if (month === undefined) {
        text(rtl ? 'רשימות' : 'Notes', margin, margin, gridWidth, h * 0.1, 24);
        continue;
      }
      const data = calendarMonth(opts, month, doc.meta.lang);
      text(data.title, margin, margin, gridWidth, h * 0.045, 25, { fontWeight: 700 });
      if (data.subtitle) text(data.subtitle, margin, h * 0.09, gridWidth, h * 0.025, 10, { color: '#58635c' });
      elements.push(makePhotoElement({ frame: frameMm(x0 + margin, h * 0.13, gridWidth, h * 0.30), z: 1 }));
      for (let i = 0; i < 7; i++) {
        const weekday = (i + opts.weekStart) % 7;
        const x = margin + (rtl ? 6 - i : i) * col;
        text(weekdays[weekday]!, x, gridY - h * 0.045, col, h * 0.035, 10, { align: 'center', fontWeight: 600 });
      }
      // Six rows also cover long months starting late in the week.
      for (let i = 0; i < 42; i++) {
        const logicalCol = i % 7;
        const x = margin + (rtl ? 6 - logicalCol : logicalCol) * col;
        const y = gridY + Math.floor(i / 7) * row;
        const day = data.days[i - data.offset];
        elements.push(makeShapeElement({ frame: frameMm(x0 + x, y, col, row), locked: true, z: 0,
          fill: day?.holidays.length ? '#fff3e6' : day?.weekday === 6 ? '#f3f5f4' : '#ffffff', stroke: '#d9ddd9', strokeWidthMm: 0.15 }));
        if (!day) continue;
        const inset = col * 0.06;
        text(day.primary, x + inset, y + row * 0.04, col - inset * 2, row * 0.27, 13, { fontWeight: 600 });
        if (day.secondary) text(day.secondary, x + inset, y + row * 0.32, col - inset * 2, row * 0.2, 6.5, { color: '#58635c' });
        if (day.holidays.length) text(day.holidays.join(' · '), x + inset, y + row * 0.54, col - inset * 2, row * 0.44, 6, { color: '#874728' });
      }
      if (opts.system === 'hebrew' || opts.dualDates || opts.jewishHolidays) {
        text(rtl ? 'תאריכים עבריים וחגים מתחילים בערב הקודם' : 'Hebrew dates and holidays begin the previous evening', margin, h * 0.95, gridWidth, h * 0.025, 6, { color: '#58635c', align: 'center' });
      }
    }
    return { ...spread, elements };
  });
  return { ...doc, calendar: opts, cover: { ...doc.cover, enabled: false }, spreads };
}
